import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import type { Part, ScanRow } from '@jobshop/shared';
import {
  useAddComponent,
  useCreatePart,
  useDeletePart,
  useJob,
  useJobScans,
  useParts,
  usePatchPart,
  useRemoveComponent,
  useStationName,
} from '../../api/hooks';
import { useSession } from '../../auth/useUser';
import { JobNav } from '../jobs/JobNav';
import { Card, CardHeader, Empty, ErrorNote, Field, Modal, Spinner } from '../../components/ui';
import { QrPreview } from './QrPreview';

export function PartsPage() {
  const { jobId, batchId } = useParams<{ jobId: string; batchId: string }>();
  const job = useJob(jobId);
  const parts = useParts(batchId);
  const scans = useJobScans(jobId);
  const session = useSession();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Part | undefined>();

  // A scan under the part it belongs to answers "did that go through?" on the spot,
  // instead of sending the reader to the job's scan history to find out. Absence still
  // proves nothing — labels are sparse by design — so nothing is drawn for a part with
  // no scans.
  const scansByPart = useMemo(() => {
    const map = new Map<string, ScanRow[]>();
    for (const scan of scans.data?.scans ?? []) {
      if (!scan.partId) continue;
      const list = map.get(scan.partId);
      if (list) list.push(scan);
      else map.set(scan.partId, [scan]);
    }
    return map;
  }, [scans.data]);

  const batch = job.data?.batches.find((b) => b._id === batchId);
  const multiBatch = (job.data?.batches.length ?? 0) > 1;

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <Link to={`/jobs/${jobId}`} className="text-ink-400 hover:underline">
                {job.data?.name ?? 'Job'}
              </Link>
              {multiBatch && batch ? (
                <span className="text-ink-500">· {batch.name}</span>
              ) : null}
              <JobNav jobId={jobId!} current="parts" partsBatchId={batchId} />
            </span>
          }
          subtitle={
            parts.data
              ? // Coverage stated honestly, always.
                `${parts.data.tracked} of ${parts.data.total} parts carry a printed QR label`
              : undefined
          }
          actions={
            <>
              <Link className="btn-secondary" to={`/jobs/${jobId}/batches/${batchId}/labels`}>
                QR Label sheet
              </Link>
              {session.can('planner') ? (
                <button className="btn-primary" onClick={() => setAdding(true)}>
                  Add part
                </button>
              ) : null}
            </>
          }
        />

        {parts.isLoading ? <Spinner /> : null}
        {parts.error ? (
          <div className="p-4">
            <ErrorNote error={parts.error} />
          </div>
        ) : null}
        {parts.data?.parts.length === 0 ? <Empty>No parts on this batch yet.</Empty> : null}

        {parts.data && parts.data.parts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Code</th>
                  <th className="w-1/2 px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Label</th>
                  <th className="px-4 py-2 font-medium">Components</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {parts.data.parts.map((part) => (
                  <PartRow
                    key={part._id}
                    part={part}
                    batchId={batchId!}
                    scans={scansByPart.get(part._id) ?? []}
                    onEdit={() => setEditing(part)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      <AddPartModal batchId={batchId!} open={adding} onClose={() => setAdding(false)} />
      <PartEditor
        batchId={batchId!}
        part={editing}
        onClose={() => setEditing(undefined)}
      />
    </div>
  );
}

function PartRow({
  part,
  batchId,
  scans,
  onEdit,
}: {
  part: Part;
  batchId: string;
  scans: ScanRow[];
  onEdit: () => void;
}) {
  const session = useSession();
  const patch = usePatchPart(batchId);
  const remove = useDeletePart(batchId);
  const stationName = useStationName();

  return (
    <>
      <tr
        className={clsx(
          'hover:bg-ink-50',
          // The scans belong to the row above them, so the border goes under the pair.
          scans.length > 0 ? '' : 'border-b border-ink-100 last:border-0',
        )}
      >
        <td className="px-4 py-2 font-medium">{part.code}</td>
        <td className="px-4 py-2 text-ink-700">{part.name ?? '—'}</td>
        <td className="px-4 py-2">
          <label className="inline-flex items-center gap-2 text-xs text-ink-600">
            <input
              type="checkbox"
              checked={part.tracked}
              disabled={!session.can('planner')}
              onChange={(e) => patch.mutate({ id: part._id, input: { tracked: e.target.checked } })}
            />
            {part.tracked ? 'tracked' : 'not tracked'}
          </label>
        </td>
        <td className="px-4 py-2 text-ink-600">
          {part.components.length === 0 ? '—' : part.components.map((c) => c.code).join(', ')}
        </td>
        <td className="px-4 py-2 text-right">
          <button className="btn-ghost" onClick={onEdit}>
            Open
          </button>
          {session.can('planner') ? (
            <button
              className="btn-ghost text-red-700"
              onClick={() => {
                if (confirm(`Delete part ${part.code}?`)) remove.mutate(part._id);
              }}
            >
              Delete
            </button>
          ) : null}
        </td>
      </tr>
      {/* Deliberately quiet: evidence beside the part, not a second table. */}
      {scans.length > 0 ? (
        <tr className="border-b border-ink-100 last:border-0">
          <td />
          <td className="px-4 pb-2 text-xs text-ink-500" colSpan={4}>
            <ul className="space-y-0.5">
              {scans.map((scan) => (
                <li key={scan._id}>
                  <span className="text-emerald-700">✓</span>{' '}
                  {scan.stationKey ? stationName(scan.stationKey) : 'no station'}
                  {scan.componentCode ? ` · component ${scan.componentCode}` : ''}
                  {' · '}
                  {new Date(scan.at).toLocaleString()}
                  {' · '}
                  {scan.user}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function AddPartModal({
  batchId,
  open,
  onClose,
}: {
  batchId: string;
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreatePart(batchId);
  const [form, setForm] = useState({
    code: '',
    name: '',
    finished: '',
    tracked: false,
  });

  return (
    <Modal open={open} onClose={onClose} title="Add a part">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(
            {
              code: form.code.trim(),
              ...(form.name ? { name: form.name } : {}),
              ...(form.finished ? { finished: form.finished } : {}),
              tracked: form.tracked,
            },
            { onSuccess: onClose },
          );
        }}
      >
        <Field label="Part code" hint="Unique across the whole job, e.g. R1C13.">
          <input
            className="field"
            required
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
          />
        </Field>
        <Field label="Name">
          <input
            className="field"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.tracked}
            onChange={(e) => setForm({ ...form, tracked: e.target.checked })}
          />
          Print a QR label for this part
        </label>

        <ErrorNote error={create.error} />
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={create.isPending}>
            Add part
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Components, label on/off, QR preview. */
function PartEditor({
  batchId,
  part,
  onClose,
}: {
  batchId: string;
  part: Part | undefined;
  onClose: () => void;
}) {
  const session = useSession();
  const patch = usePatchPart(batchId);
  const addComponent = useAddComponent(batchId);
  const removeComponent = useRemoveComponent(batchId);
  const [componentName, setComponentName] = useState('');

  if (!part) return null;

  return (
    <Modal open onClose={onClose} title={`Part ${part.code}`}>
      <div className="space-y-4">
        <div className="flex gap-4">
          <QrPreview qrText={part.qrText} size={110} />
          <div className="min-w-0 flex-1 space-y-1 text-sm">
            <p className="font-medium">{part.name ?? '(no name)'}</p>
            <p className="break-all font-mono text-xs text-ink-500">{part.qrText}</p>
            <label className="mt-2 flex items-center gap-2">
              <input
                type="checkbox"
                checked={part.tracked}
                disabled={!session.can('planner')}
                onChange={(e) =>
                  patch.mutate({ id: part._id, input: { tracked: e.target.checked } })
                }
              />
              Include on the printed label sheet
            </label>
          </div>
        </div>

        <div>
          <h3 className="label">Components</h3>
          {part.components.length === 0 ? (
            <p className="text-sm text-ink-500">None.</p>
          ) : (
            <ul className="space-y-2">
              {part.components.map((c) => (
                <li key={c.code} className="flex items-center gap-3">
                  <QrPreview qrText={c.qrText} size={56} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm">{c.name ?? `Component ${c.code}`}</span>
                    <span className="block break-all font-mono text-xs text-ink-500">
                      {c.qrText}
                    </span>
                  </span>
                  {session.can('planner') ? (
                    <button
                      className="btn-ghost text-red-700"
                      onClick={() => removeComponent.mutate({ partId: part._id, code: c.code })}
                    >
                      Remove
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          {session.can('planner') ? (
            <form
              className="mt-3 flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (!componentName.trim()) return;
                addComponent.mutate(
                  { partId: part._id, name: componentName.trim() },
                  { onSuccess: () => setComponentName('') },
                );
              }}
            >
              <input
                className="field"
                placeholder="New component name"
                value={componentName}
                onChange={(e) => setComponentName(e.target.value)}
              />
              <button className="btn-secondary" disabled={addComponent.isPending}>
                Add
              </button>
            </form>
          ) : null}
        </div>

        <ErrorNote error={patch.error ?? addComponent.error ?? removeComponent.error} />
      </div>
    </Modal>
  );
}
